import fs from 'node:fs';
import path from 'node:path';
import { performance } from 'node:perf_hooks';
import type { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import { redactEnvFile } from '../../utils/env-parser.js';
import { optionalNonEmptyString } from './_zod-helpers.js';
import { EmbeddingPipeline } from '../../ai/embedding-pipeline.js';
import { getReindexStats } from '../../daemon/reindex-stats.js';
import { repairIndex, type RepairMode } from '../../db/repair.js';
import { verifyIndex } from '../../db/verify.js';
import { LOCKS_DIR, projectHash } from '../../global.js';
import { IndexingPipeline } from '../../indexer/pipeline.js';
import { buildProjectContext } from '../../indexer/project-context.js';
import { shouldSkipRecentReindex } from '../../indexer/recent-reindex-cache.js';
import { logger } from '../../logger.js';
import type { ServerContext } from '../../server/types.js';
import { LockError, withLock } from '../../utils/pid-lock.js';
import { verifyDecision } from '../../memory/decision-verification.js';
import { relativizeUnderRoot } from '../../utils/path-relativize.js';
import { checkFileForDuplicates } from '../analysis/duplication.js';
import { getMinimalContext } from '../project/minimal-context.js';
import { getIndexHealth, getProjectMap } from '../project/project.js';

export function registerCoreTools(server: McpServer, ctx: ServerContext): void {
  const {
    store,
    registry,
    config,
    projectRoot,
    guardPath,
    j,
    jh,
    journal,
    vectorStore,
    embeddingService,
    progress,
    onPipelineEvent,
  } = ctx;

  // --- Core Tools (always registered) ---

  server.tool(
    'get_index_health',
    'Get index status, statistics, health, and pipeline progress (indexing, summarization, embedding). Read-only, no side effects. Use to verify the index is ready before running queries. Returns JSON: { totalFiles, totalSymbols, languages, frameworks, pipelineProgress, embedding }.',
    {},
    async () => {
      const result = getIndexHealth(store, config);
      if (ctx.progress) {
        result.progress = ctx.progress.snapshot();
      }
      return { content: [{ type: 'text', text: j(result) }] };
    },
  );

  server.tool(
    'reindex',
    'Trigger (re)indexing of the project or a subdirectory. Mutates the local index (SQLite). Use after major file changes; for single-file updates prefer register_edit instead. The optional `postprocess` flag controls how much work runs after raw symbol extraction: "full" (default) does everything; "minimal" skips LSP enrichment + env-var scan + git history snapshots (~30-50% faster on warm CI runs); "none" also skips edge resolution and gives you raw symbols only. Idempotent — safe to re-run. Returns JSON: { status, totalFiles, indexed, skipped, errors, durationMs, postprocess }.',
    {
      path: z
        .string()
        .max(512)
        .optional()
        .describe('Subdirectory to index (default: project root)'),
      force: z.boolean().optional().describe('Skip hash check and reindex all files'),
      postprocess: z
        .enum(['full', 'minimal', 'none'])
        .optional()
        .describe(
          'Postprocess level. full = everything (default). minimal = skips LSP/env/snapshots. none = also skips edge resolution.',
        ),
    },
    async ({ path: indexPath, force, postprocess }) => {
      if (indexPath) {
        const blocked = guardPath(indexPath);
        if (blocked) return blocked;
      }
      logger.info({ path: indexPath, force, postprocess }, 'Reindex requested');
      // Pass `progress` so this run updates the same ProgressState that
      // `get_index_health` reports. Without it, `progress.indexing` kept
      // showing whatever the last watcher batch did — "completed 276/276"
      // right after a 2069-file forced reindex (TRA-231).
      const pipeline = new IndexingPipeline(
        store,
        registry,
        config,
        projectRoot,
        progress ?? undefined,
      );

      try {
        const result = await withLock(
          { lockDir: LOCKS_DIR, name: `${projectHash(projectRoot)}-reindex`, op: 'reindex' },
          async () =>
            indexPath
              ? pipeline.indexFiles([indexPath], { postprocess })
              : pipeline.indexAll(force ?? false, { postprocess }),
        );
        return { content: [{ type: 'text', text: j({ status: 'completed', ...result }) }] };
      } catch (e) {
        if (e instanceof LockError) {
          return {
            content: [
              {
                type: 'text',
                text: j({
                  status: 'busy',
                  error: 'reindex_in_progress',
                  message: e.message,
                  holder: e.holder,
                }),
              },
            ],
            isError: true,
          };
        }
        throw e;
      }
    },
  );

  server.tool(
    'embed_repo',
    'Precompute and cache symbol embeddings for semantic / hybrid search. Also computed lazily on first semantic query — call this once after a fresh index to avoid that latency spike. Requires an AI provider (ollama/openai) enabled in config. force=true recomputes all embeddings. Mutates the vector store; idempotent. Returns JSON: { status, indexed_this_run, total_embedded, coverage_pct, duration_ms }. Failed batches return status "error" with a diagnostic and failed_batches count, never a silent 0-coverage "completed".',
    {
      batch_size: z
        .number()
        .int()
        .min(1)
        .max(500)
        .optional()
        .describe('Symbols per embedding API batch (default 50)'),
      force: z
        .boolean()
        .optional()
        .describe('Drop existing embeddings and re-embed everything (default false — incremental)'),
    },
    async ({ batch_size, force }) => {
      if (!vectorStore || !embeddingService) {
        return {
          content: [
            {
              type: 'text',
              text: j({
                status: 'disabled',
                message:
                  'Semantic search disabled. Enable an AI provider in trace-mcp.config.json (ai.enabled=true, ai.provider=ollama|openai).',
              }),
            },
          ],
          isError: true,
        };
      }
      logger.info({ force, batch_size }, 'embed_repo requested');

      // P0.1 — provider/model drift detection. When the active provider or
      // model differs from the one stamped on the index, we either:
      //   - auto-promote to force=true (default; ai.autoRebuildOnProviderMismatch=true)
      //   - or refuse and surface ProviderMismatchError to the caller
      // Skipped entirely when the user already asked for force=true (they
      // intend to rewrite the index regardless of what's stored).
      const autoRebuild = ctx.config.ai?.autoRebuildOnProviderMismatch ?? true;
      let effectiveForce = !!force;
      if (!effectiveForce) {
        try {
          const active = {
            provider: embeddingService.providerName?.() ?? 'unknown',
            model: embeddingService.modelName(),
          };
          const check = vectorStore.checkProviderMatch(active);
          if (check.kind === 'mismatch') {
            if (autoRebuild) {
              logger.warn(
                { stored: check.stored, active: check.active },
                'embed_repo: embedding provider/model mismatch detected — auto-rebuilding index (force=true)',
              );
              effectiveForce = true;
            } else {
              return {
                content: [
                  {
                    type: 'text',
                    text: j({
                      status: 'error',
                      error: 'provider_mismatch',
                      stored: check.stored,
                      active: check.active,
                      message:
                        `Embedding provider mismatch: index built with ${check.stored.provider}/${check.stored.model}, ` +
                        `active config uses ${check.active.provider}/${check.active.model}. ` +
                        `Re-run with force=true to rebuild, or set ai.autoRebuildOnProviderMismatch=true to auto-rebuild on the next call.`,
                    }),
                  },
                ],
                isError: true,
              };
            }
          }
        } catch (e) {
          // Mismatch detection must never break embed_repo. Fall through
          // and let the pipeline handle whatever it finds.
          logger.warn({ error: e }, 'embed_repo: provider-match probe failed; continuing');
        }
      }

      const pipeline = new EmbeddingPipeline(
        store,
        embeddingService,
        vectorStore,
        progress ?? undefined,
        { autoRebuildOnProviderMismatch: autoRebuild },
      );
      // Explicit user request — never a silent no-op because the background
      // breaker is still in its cooldown from an earlier failure (TRA-812).
      pipeline.resetCircuitBreaker();
      try {
        const startedAt = Date.now();
        // R09 v2: emit embed_started before the work begins. The total is
        // approximated from current symbol count when force=true (full
        // reindex) or unknown for incremental runs.
        const totalSymbolsForStart = store.getStats().totalSymbols;
        onPipelineEvent({
          type: 'embed_started',
          total: effectiveForce ? totalSymbolsForStart : undefined,
        });
        // R09 v2: bridge the pipeline's existing ProgressState onto the
        // SSE bus as embed_progress events. The daemon throttles these
        // to ~5/s per project; terminal events are never throttled.
        const progressUnsub = progress?.onUpdate((pipelineName, snapshot) => {
          if (pipelineName !== 'embedding') return;
          onPipelineEvent({
            type: 'embed_progress',
            processed: snapshot.processed,
            total: snapshot.total,
          });
        });
        let indexed: number;
        try {
          indexed = await withLock(
            { lockDir: LOCKS_DIR, name: `${projectHash(projectRoot)}-embed`, op: 'embed_repo' },
            async () =>
              effectiveForce ? pipeline.reindexAll() : pipeline.indexUnembedded(batch_size ?? 50),
          );
        } finally {
          progressUnsub?.();
        }
        const totalEmbedded = vectorStore.count();
        const totalSymbols = store.getStats().totalSymbols;
        // R09 v2: terminal event — never throttled by the daemon.
        onPipelineEvent({
          type: 'embed_completed',
          duration_ms: Date.now() - startedAt,
          embedded: totalEmbedded,
        });

        // Surface swallowed batch failures. Without this, a dimension mismatch
        // (config dim != model dim) or a tripped circuit breaker leaves
        // embed_repo reporting "completed" with 0 coverage and no hint why.
        const diag = pipeline.getLastRunDiagnostics();
        const coveragePct = totalSymbols > 0 ? Math.round((totalEmbedded / totalSymbols) * 100) : 0;
        const embeddedNothingButHadWork = indexed === 0 && totalEmbedded === 0 && totalSymbols > 0;
        if (diag.failedBatches > 0 || diag.breakerTripped || embeddedNothingButHadWork) {
          const active = {
            provider: embeddingService.providerName?.() ?? 'unknown',
            model: embeddingService.modelName(),
            dimensions: embeddingService.dimensions(),
          };
          const hint = diag.dimensionMismatch
            ? `Embedding dimension mismatch: the ${active.provider} model "${active.model}" returned vectors that don't match the ${active.dimensions}-dim vector store. ` +
              `Set ai.embedding_dimensions to the model's real dimension (or remove it to auto-detect), then re-run embed_repo with force=true.`
            : diag.breakerTripped
              ? `The embedding service failed repeatedly and the circuit breaker tripped. Check that the ${active.provider} provider at the configured base_url is reachable and the model "${active.model}" is available, then re-run embed_repo.`
              : `Embedding produced no vectors despite ${totalSymbols} indexed symbols. Check the ${active.provider} provider configuration and logs.`;
          logger.warn(
            { diagnostics: diag, active, totalEmbedded, totalSymbols },
            'embed_repo completed with failures — surfacing to caller',
          );
          return {
            content: [
              {
                type: 'text',
                text: j({
                  status: 'error',
                  error: diag.dimensionMismatch
                    ? 'dimension_mismatch'
                    : diag.breakerTripped
                      ? 'embedding_service_failing'
                      : 'no_embeddings_produced',
                  message: hint,
                  failed_batches: diag.failedBatches,
                  breaker_tripped: diag.breakerTripped,
                  last_error: diag.lastError,
                  indexed_this_run: indexed,
                  total_embedded: totalEmbedded,
                  total_symbols: totalSymbols,
                  coverage_pct: coveragePct,
                  active,
                  duration_ms: Date.now() - startedAt,
                  force: effectiveForce,
                }),
              },
            ],
            isError: true,
          };
        }

        return {
          content: [
            {
              type: 'text',
              text: j({
                status: 'completed',
                indexed_this_run: indexed,
                total_embedded: totalEmbedded,
                total_symbols: totalSymbols,
                coverage_pct: coveragePct,
                duration_ms: Date.now() - startedAt,
                force: effectiveForce,
              }),
            },
          ],
        };
      } catch (e) {
        if (e instanceof LockError) {
          return {
            content: [
              {
                type: 'text',
                text: j({
                  status: 'busy',
                  error: 'embed_repo_in_progress',
                  message: e.message,
                  holder: e.holder,
                }),
              },
            ],
            isError: true,
          };
        }
        return {
          content: [
            {
              type: 'text',
              text: j({
                status: 'error',
                error: e instanceof Error ? e.message : String(e),
              }),
            },
          ],
          isError: true,
        };
      }
    },
  );

  server.tool(
    'verify_index',
    'Read-only structural check of the local SQLite index: SQLite integrity_check, foreign-key violations, required-table presence, FTS5 integrity-check, embedding dimension consistency, and orphan embedding detection. Returns a check-by-check report with status (ok/warn/error) and a suggested repair mode for any non-ok finding. Never writes. Use as a preflight before reindex/embed_repo or when search is misbehaving. Returns JSON: { ok, status, checks: [{ name, status, detail, count?, suggested_repair? }] }.',
    {},
    async () => {
      // P0.1 — pass the active embedding (when configured) so verify_index
      // can surface provider/model drift between the on-disk index and the
      // active config. Falls back to the original single-arg behaviour when
      // semantic search is disabled.
      const activeEmbedding = embeddingService
        ? {
            provider: embeddingService.providerName?.() ?? 'unknown',
            model: embeddingService.modelName(),
          }
        : undefined;
      const report = verifyIndex(store.db, activeEmbedding ? { activeEmbedding } : undefined);
      return {
        content: [{ type: 'text', text: j(report) }],
        isError: report.status === 'error',
      };
    },
  );

  server.tool(
    'repair_index',
    'Apply a targeted repair to the local SQLite index. Modes: drop-orphans (delete embedding rows whose symbol_id no longer exists), drop-vec (drop the entire vector store — search falls back to BM25; embed_repo rebuilds), rebuild-fts (drop and reload symbols_fts from the symbols table). Each mode runs in a transaction so a partial failure leaves the DB unchanged. DESTRUCTIVE — verify_index first to find out which mode is needed. Returns JSON: { mode, ok, detail, affected }.',
    {
      mode: z
        .enum(['drop-orphans', 'drop-vec', 'rebuild-fts'])
        .describe('Repair mode — see tool description for what each mode does.'),
    },
    async ({ mode }) => {
      try {
        const result = await withLock(
          { lockDir: LOCKS_DIR, name: `${projectHash(projectRoot)}-repair`, op: 'repair_index' },
          async () => repairIndex(store.db, mode as RepairMode),
        );
        return { content: [{ type: 'text', text: j(result) }] };
      } catch (e) {
        if (e instanceof LockError) {
          return {
            content: [
              {
                type: 'text',
                text: j({
                  status: 'busy',
                  error: 'index_busy',
                  message: e.message,
                  holder: e.holder,
                }),
              },
            ],
            isError: true,
          };
        }
        return {
          content: [
            {
              type: 'text',
              text: j({
                ok: false,
                mode,
                detail: e instanceof Error ? e.message : String(e),
              }),
            },
          ],
          isError: true,
        };
      }
    },
  );

  server.tool(
    'register_edit',
    'Notify trace-mcp that a file was edited. Reindexes the single file and invalidates search caches. Call after Edit/Write to keep index fresh — much lighter than full reindex. Also checks for duplicate symbols — if `_duplication_warnings` appears in the response, you may be recreating existing logic; review the referenced symbols before continuing. Mutates the index; idempotent. Returns JSON: { status, file, totalFiles, indexed, _duplication_warnings? }.',
    {
      file_path: z.string().min(1).max(512).describe('Relative path to the edited file'),
    },
    async ({ file_path: filePath }) => {
      const startedAt = performance.now();
      const blocked = guardPath(filePath);
      if (blocked) return blocked;

      // Phase 1.3 dedup: skip when the PostToolUse hook (or another caller)
      // already reindexed this file in the last 500 ms. Journaling still runs
      // so observability is preserved.
      // Key must match the HTTP handler's normalization (POSIX-relative)
      // so the two paths collide on the same file regardless of input form.
      const absForDedup = path.isAbsolute(filePath)
        ? filePath
        : path.resolve(projectRoot, filePath);
      const relForDedup = path.relative(projectRoot, absForDedup);
      const dedupKey = path.sep === '\\' ? relForDedup.split('\\').join('/') : relForDedup;
      if (shouldSkipRecentReindex(projectRoot, dedupKey)) {
        journal.record('register_edit', { file_path: filePath, skipped_recent: true }, 1);
        const elapsedMs = Math.round(performance.now() - startedAt);
        logger.info(
          {
            event: 'reindex-file',
            project: projectRoot,
            path: dedupKey,
            pathSource: 'mcp',
            skippedRecent: true,
            skippedHash: false,
            indexed: 0,
            elapsedMs,
          },
          'reindex-file telemetry',
        );
        getReindexStats().record({
          pathSource: 'mcp',
          skippedRecent: true,
          skippedHash: false,
          indexed: 0,
          elapsedMs,
        });
        return {
          content: [
            {
              type: 'text',
              text: j({
                status: 'skipped_recent',
                file: filePath,
                skipped_recent: true,
              }),
            },
          ],
        };
      }

      // Pass `progress` so this run updates the same ProgressState that
      // `get_index_health` reports. Without it, `progress.indexing` kept
      // showing whatever the last watcher batch did — "completed 276/276"
      // right after a 2069-file forced reindex (TRA-231).
      const pipeline = new IndexingPipeline(
        store,
        registry,
        config,
        projectRoot,
        progress ?? undefined,
      );
      let result: Awaited<ReturnType<typeof pipeline.indexFiles>>;
      try {
        // Share the reindex lock so a single-file edit cannot race with a
        // running full-project reindex. The two paths mutate the same SQLite
        // database (and vector store), and concurrent writers were called out
        // as a corruption risk in the original PID-guard rollout.
        result = await withLock(
          { lockDir: LOCKS_DIR, name: `${projectHash(projectRoot)}-reindex`, op: 'register_edit' },
          () => pipeline.indexFiles([filePath]),
        );
      } catch (e) {
        const elapsedMs = Math.round(performance.now() - startedAt);
        // Phase 5+7 audit fix: telemetry must cover all exits — without this
        // a failure becomes invisible to /api/stats and `daemon stats`.
        logger.warn(
          {
            event: 'reindex-file',
            project: projectRoot,
            path: dedupKey,
            pathSource: 'mcp',
            skippedRecent: false,
            skippedHash: false,
            indexed: 0,
            elapsedMs,
            err: e,
          },
          'reindex-file telemetry (error)',
        );
        getReindexStats().record({
          pathSource: 'mcp',
          skippedRecent: false,
          skippedHash: false,
          indexed: 0,
          elapsedMs,
          error: true,
        });
        if (e instanceof LockError) {
          return {
            content: [
              {
                type: 'text',
                text: j({
                  status: 'busy',
                  error: 'reindex_in_progress',
                  message: e.message,
                  holder: e.holder,
                }),
              },
            ],
            isError: true,
          };
        }
        throw e;
      }

      // Record in journal so session knows this file was edited
      journal.record('register_edit', { file_path: filePath }, 1);

      const indexed = result.indexed ?? 0;
      const skipped = result.skipped ?? 0;
      const skippedHash = indexed === 0 && skipped > 0;
      // TRA-935: split the reindex work from the time spent waiting for the
      // reindex lock. Reported as one number, a fast edit queued behind a full
      // project pass looked like a pathologically slow reindex.
      const totalMs = Math.round(performance.now() - startedAt);
      const elapsedMs = result.durationMs ?? totalMs;
      const queuedMs = Math.max(0, totalMs - elapsedMs);
      logger.info(
        {
          event: 'reindex-file',
          project: projectRoot,
          path: dedupKey,
          pathSource: 'mcp',
          skippedRecent: false,
          // Hash gate: indexFiles reports a skipped row when the content hash
          // matched a previous run — extract + edge resolution were bypassed.
          skippedHash,
          indexed,
          elapsedMs,
          queuedMs,
        },
        'reindex-file telemetry',
      );
      getReindexStats().record({
        pathSource: 'mcp',
        skippedRecent: false,
        skippedHash,
        indexed,
        elapsedMs,
        queuedMs,
      });

      // Best-effort duplication check — never fails register_edit
      let dupWarnings: {
        message: string;
        score: number;
        duplicate_symbol_id: string;
        duplicate_file: string;
      }[] = [];
      try {
        const dup = checkFileForDuplicates(store, store.db, filePath, {
          threshold: 0.7,
          maxResults: 5,
        });
        dupWarnings = dup.warnings.map((w) => ({
          message: `"${w.source_name}" is similar to "${w.duplicate_name}" in ${w.duplicate_file}:${w.duplicate_line ?? '?'}`,
          score: w.score,
          duplicate_symbol_id: w.duplicate_symbol_id,
          duplicate_file: w.duplicate_file,
        }));
      } catch {
        /* non-fatal */
      }

      // Task 3 — opportunistic decision staleness re-check. After the edited
      // file is reindexed, re-verify any decisions linked to it so the agent
      // learns immediately when a decision's anchored symbol was renamed/
      // deleted or its source changed since capture. Best-effort: never fails
      // register_edit, and only runs when a decision store is wired.
      const staleDecisions: Array<{
        id: number;
        title: string;
        verification: string;
      }> = [];
      try {
        const ds = ctx.decisionStore;
        if (ds) {
          const relPath = relativizeUnderRoot(filePath, projectRoot) ?? dedupKey;
          const linked = ds.getDecisionsForFile(relPath);
          for (const d of linked) {
            if (!d.symbol_id) continue;
            const v = verifyDecision(d, store, projectRoot);
            if (v.stale) {
              staleDecisions.push({ id: d.id, title: d.title, verification: v.verification });
            }
          }
        }
      } catch {
        /* non-fatal — verification is advisory only */
      }

      return {
        content: [
          {
            type: 'text',
            text: j({
              status: 'reindexed',
              file: filePath,
              ...result,
              ...(dupWarnings.length > 0 ? { _duplication_warnings: dupWarnings } : {}),
              ...(staleDecisions.length > 0 ? { _stale_decisions: staleDecisions } : {}),
            }),
          },
        ],
      };
    },
  );

  server.tool(
    'get_minimal_context',
    'Single-call orientation context (~150 tokens). Returns project shape, top 3 risk hotspots, top 3 PageRank-central files, top 3 communities, and 3-5 task-routed next-tool suggestions. Use at session start instead of chaining get_project_map + get_pagerank + get_risk_hotspots + get_communities. The optional `task` argument biases the suggestions toward review / refactor / debug / add_feature / understand. Read-only. Returns JSON: { project, health, communities, next_steps }.',
    {
      task: z
        .string()
        .max(500)
        .optional()
        .describe(
          'What you are about to do — drives next_steps ranking. Omit for the "understand" suggestion set.',
        ),
      intent: z
        .enum(['understand', 'review', 'refactor', 'debug', 'add_feature'])
        .optional()
        .describe('Explicit intent override. Wins over keyword inference from `task`.'),
    },
    async ({ task, intent }) => {
      const projectCtx = buildProjectContext(projectRoot);
      const result = getMinimalContext(store, registry, config, projectRoot, projectCtx, {
        task,
        intent,
      });
      return { content: [{ type: 'text', text: j(result) }] };
    },
  );

  server.tool(
    'get_project_map',
    'Get project overview: detected frameworks, languages, file counts, structure. Read-only, no side effects. Call with summary_only=true at session start to orient yourself before diving into code. Use instead of manual ls/find. Returns JSON: { frameworks, languages, fileCount, symbolCount, structure }.',
    {
      summary_only: z
        .boolean()
        .optional()
        .describe('Return only framework list + counts (default false)'),
    },
    async ({ summary_only }) => {
      const ctx = buildProjectContext(projectRoot);
      const result = getProjectMap(store, registry, summary_only ?? false, ctx);
      return { content: [{ type: 'text', text: jh('get_project_map', result) }] };
    },
  );

  // --- Env Vars Tool ---

  server.tool(
    'get_env_vars',
    'List environment variable keys from .env files with inferred value types/formats. Never exposes actual values — only keys, types, and formats (url/email/ip/path/uuid/json/base64/csv/dsn/etc). Safe for secrets. Pass `redacted: true` with `file` for a line-by-line redacted view of one file (keys + type hints, no values, preserves order/comments). Returns JSON grouped by file: { [file]: [{ key, type, format, comment }] }.',
    {
      pattern: z
        .string()
        .max(256)
        .optional()
        .describe('Filter keys by pattern (e.g. "DB_" or "REDIS")'),
      file: optionalNonEmptyString(512).describe('Filter by specific .env file path'),
      redacted: z
        .boolean()
        .optional()
        .describe(
          'Return a line-by-line redacted text view of the file (keys + type hints, no values). Requires `file`.',
        ),
    },
    async ({ pattern, file, redacted }) => {
      if (redacted) {
        if (!file) {
          return {
            content: [
              {
                type: 'text',
                text: '`redacted: true` requires `file`. Pass the .env path to redact, e.g. { file: ".env", redacted: true }.',
              },
            ],
          };
        }
        // Defence against path-injection through user-controlled `file`.
        // Two independent gates:
        //
        //   1. Containment: the resolved absolute path must be inside
        //      projectRoot. We use the prefix-startsWith pattern that
        //      CodeQL's `js/path-injection` rule recognises as a
        //      sanitiser (path.resolve + startsWith(base + sep) check).
        //      Out-of-tree env files (e.g. ~/.trace-mcp/launcher.env)
        //      are intentionally unreachable via redacted-mode reads;
        //      the default get_env_vars view still works for them since
        //      it reads from the indexed DB, not the filesystem.
        //
        //   2. Allowlist: the resolved path must be one the indexer
        //      already classified as a .env file. Prevents redacted-mode
        //      from doubling as a generic in-tree file reader.
        const safePath = path.resolve(projectRoot, file);
        const baseWithSep = projectRoot.endsWith(path.sep) ? projectRoot : projectRoot + path.sep;
        if (safePath !== projectRoot && !safePath.startsWith(baseWithSep)) {
          return {
            content: [
              {
                type: 'text',
                text: `Refusing to read "${file}": path escapes the project root.`,
              },
            ],
          };
        }
        const indexedAbsPaths = new Set(
          store
            .getAllEnvVars()
            .map((row) =>
              path.isAbsolute(row.file_path)
                ? row.file_path
                : path.resolve(projectRoot, row.file_path),
            ),
        );
        if (!indexedAbsPaths.has(safePath)) {
          return {
            content: [
              {
                type: 'text',
                text: `No indexed .env file matches "${file}". Use get_env_vars without \`redacted\` to list known files, or run indexing.`,
              },
            ],
          };
        }
        let content: string;
        try {
          content = fs.readFileSync(safePath, 'utf8');
        } catch (err) {
          const reason = err instanceof Error ? err.message : String(err);
          return {
            content: [{ type: 'text', text: `Cannot read ${file}: ${reason}` }],
          };
        }
        return { content: [{ type: 'text', text: redactEnvFile(content) }] };
      }

      let vars = pattern ? store.searchEnvVars(pattern) : store.getAllEnvVars();

      if (file) {
        vars = vars.filter((v) => v.file_path === file || v.file_path.endsWith(file));
      }

      if (vars.length === 0) {
        return {
          content: [
            { type: 'text', text: 'No env vars found. Run indexing first or adjust the filter.' },
          ],
        };
      }

      // Group by file
      const grouped: Record<
        string,
        { key: string; type: string; format: string | null; comment: string | null }[]
      > = {};
      for (const v of vars) {
        const arr = (grouped[v.file_path] ??= []);
        arr.push({
          key: v.key,
          type: v.value_type,
          format: v.value_format,
          comment: v.comment,
        });
      }

      return { content: [{ type: 'text', text: j(grouped) }] };
    },
  );
}
